import React, { useState } from 'react';
import { Combobox } from '@headlessui/react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Type-to-filter dropdown — same job as a native <select> but lets the user
 * narrow a long options list by typing instead of scrolling.
 *
 * Props:
 *   options     — [{ value, label }]
 *   value       — currently selected value
 *   onChange    — (value) => void
 *   placeholder — shown when nothing is typed/selected
 *   style       — merged into the input's inline style (so callers can match
 *                 surrounding form fields, e.g. maxWidth)
 */
export default function SearchableSelect({ options, value, onChange, placeholder = 'Search…', style }) {
  const [query, setQuery] = useState('');
  const selected = options.find(o => o.value === value) || null;

  const filtered = query === ''
    ? options
    : options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()));

  return (
    <Combobox value={value} onChange={onChange}>
      <div style={{ position: 'relative', ...style }}>
        <Combobox.Input
          style={{
            width: '100%', padding: '7px 30px 7px 10px', border: '1px solid var(--cb-d1d5db)',
            borderRadius: 4, fontSize: '.88rem', boxSizing: 'border-box', background: 'var(--cb-ffffff)',
          }}
          displayValue={() => (selected ? selected.label : '')}
          placeholder={placeholder}
          onChange={e => setQuery(e.target.value)}
        />
        <Combobox.Button style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer' }}>
          <ChevronDown size={14} color="var(--c-6b7280)" />
        </Combobox.Button>
        <Combobox.Options style={{
          position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4,
          maxHeight: 240, overflowY: 'auto', background: 'var(--cb-ffffff)', border: '1px solid var(--cb-d1d5db)',
          borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', padding: 4, listStyle: 'none',
        }}>
          {filtered.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '.85rem', color: 'var(--c-9ca3af)' }}>No matches</div>
          )}
          {filtered.map(o => (
            <Combobox.Option key={o.value} value={o.value} style={{ listStyle: 'none' }}>
              {({ active, selected: isSelected }) => (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '7px 10px', borderRadius: 4, fontSize: '.88rem', cursor: 'pointer',
                  background: active ? 'var(--cb-eff6ff)' : 'transparent', color: 'var(--c-111111)',
                }}>
                  <span>{o.label}</span>
                  {isSelected && <Check size={14} color="var(--c-1e3a8a)" />}
                </div>
              )}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}
